import { create } from 'zustand';
import { couldHaveSurcharge, getServiceHasSurcharge, } from '../utils';
/**
 * Merge flat service metadata (clinicIds, surcharges, departmentId, etc.) into
 * each tree entry. The tree is the source of truth for price and partner
 * campaign fields since those are partner-adjusted when the tree was fetched
 * with a partnerId.
 */
function mergeTreeWithFlat(treeService, flatService) {
    if (!flatService)
        return treeService;
    return { ...flatService, ...treeService };
}
/** Build a shortId → merged Service map by walking the enriched tree. */
function buildServiceMap(tree) {
    const map = new Map();
    const visit = (nodes) => {
        nodes.forEach((node) => {
            node.services.forEach((s) => map.set(s.shortId, s));
            visit(node.subCategories);
        });
    };
    visit(tree);
    return map;
}
function enrichNode(node, serviceMap) {
    return {
        ...node,
        services: (node.services ?? []).map((s) => mergeTreeWithFlat(s, serviceMap.get(s.shortId))),
        subCategories: (node.subCategories ?? []).map((sub) => enrichNode(sub, serviceMap)),
    };
}
function enrichTree(tree, serviceMap) {
    return tree.map((node) => enrichNode(node, serviceMap));
}
export const useServiceTreeStore = create()((set, get) => ({
    serviceTree: [],
    serviceMap: new Map(),
    specialities: [],
    links: [],
    selectedService: undefined,
    getLinksByVerticalShortId: (shortId) => {
        const { serviceTree } = get();
        return serviceTree.find((speciality) => speciality.shortId === shortId)
            ?.links;
    },
    getServiceByShortId: (shortId) => {
        const serviceMap = get().serviceMap;
        if (serviceMap.size > 0)
            return serviceMap.get(shortId);
    },
    getServiceUuid: (shortId) => {
        const service = get().getServiceByShortId(shortId);
        return service?.id || '';
    },
    getServiceHasSurchargeForShortId: (shortId) => {
        return getServiceHasSurcharge(get().getServiceByShortId(shortId));
    },
    getSurcharge: (shortId, dayType = 'day', isEvening = false, useFallbackResult = false) => {
        const fallbackResult = {
            surchargePrice: 0,
            surchargeType: undefined,
        };
        if (!shortId ||
            useFallbackResult ||
            !couldHaveSurcharge(dayType, isEvening))
            return fallbackResult;
        const service = get().getServiceByShortId(shortId);
        if (!getServiceHasSurcharge(service)) {
            return fallbackResult;
        }
        const isWeekendOrHoliday = dayType === 'holiday' || dayType === 'weekend';
        const weekendSurcharge = service?.weekendSurcharge ?? 0;
        const eveningSurcharge = service?.eveningSurcharge ?? 0;
        if (isWeekendOrHoliday && weekendSurcharge > 0) {
            return {
                surchargePrice: weekendSurcharge,
                surchargeType: dayType,
            };
        }
        if (isEvening && eveningSurcharge > 0) {
            return {
                surchargePrice: eveningSurcharge,
                surchargeType: 'evening',
            };
        }
        return fallbackResult;
    },
    setServiceTree: (serviceTree) => {
        const serviceMap = new Map();
        serviceTree
            .map(({ subCategories, services }) => subCategories
            .map(({ services }) => services)
            .concat(services)
            .flat())
            .flat()
            .forEach((service) => serviceMap.set(service.shortId, service));
        set(() => ({ serviceTree, serviceMap }));
    },
    setServiceData: ({ tree, services }) => {
        const flatMap = new Map();
        services.forEach((service) => flatMap.set(service.shortId, service));
        const enriched = enrichTree(tree, flatMap);
        set(() => ({
            serviceTree: enriched,
            serviceMap: buildServiceMap(enriched),
        }));
    },
    getSpecialities: (vertical) => {
        const { serviceTree, serviceMap } = get();
        const verticalNode = serviceTree.find(({ shortId }) => shortId === vertical) ||
            {};
        if (!verticalNode.subCategories || !verticalNode.services)
            return [];
        const result = [...verticalNode.subCategories];
        verticalNode.services.forEach((service) => {
            const fullService = serviceMap.get(service.shortId) ?? service;
            result.push({
                ...verticalNode,
                id: fullService.id,
                name: fullService.displayName,
                shortId: fullService.shortId,
                services: [fullService],
                order: fullService.sortOrder ?? 0,
            });
        });
        return result.sort((a, b) => a.order - b.order);
    },
    getServiceName: (serviceId, locale) => {
        const serviceMap = get().serviceMap;
        return serviceMap.get(serviceId)?.displayName[locale];
    },
    getSpecialityName(verticalId, categoryId, locale) {
        const serviceTree = get().serviceTree;
        const vertical = serviceTree.find(({ shortId }) => shortId === verticalId);
        const speciality = vertical?.subCategories.find(({ shortId }) => shortId === categoryId);
        const name = speciality?.name[locale];
        if (name)
            return name;
        // Fall back to vertical name, if no speciality name is found
        return vertical?.name[locale];
    },
    setSelectedService: (service) => {
        set(() => ({ selectedService: service }));
    },
    clearSelectedService: () => {
        set(() => ({ selectedService: undefined }));
    },
    getServicePrice: (shortId) => {
        const serviceMap = get().serviceMap;
        if (serviceMap.size <= 0)
            return undefined;
        return serviceMap.get(shortId)?.price;
    },
    // This is for overriding the price of a service,
    // intended for when there is a price difference between the partner campaign
    // the user chooses and the price from service tree
    setServicePrice: (shortId, price) => {
        const serviceMap = get().serviceMap;
        const service = serviceMap.get(shortId);
        if (service) {
            service.price = price;
            serviceMap.set(shortId, service);
        }
        set(() => ({ serviceMap }));
    },
    removePartnerCampaignFromService: (shortId) => {
        const serviceMap = get().serviceMap;
        const service = serviceMap.get(shortId);
        if (service) {
            delete service.partnerId;
            delete service.membershipCampaignId;
            delete service.partnerName;
            serviceMap.set(shortId, service);
        }
        set(() => ({ serviceMap }));
    },
    setPartnerCampaignForService: (shortId, partnerId, membershipCampaignId, partnerName) => {
        const serviceMap = get().serviceMap;
        const service = serviceMap.get(shortId);
        if (service) {
            service.partnerId = partnerId;
            service.membershipCampaignId = membershipCampaignId;
            service.partnerName = partnerName;
            serviceMap.set(shortId, service);
        }
        set(() => ({ serviceMap }));
    },
    // This is a way of getting all clinic ids from the service tree
    // and is used to match clinic ids with the currently selected service
    getClinicIdsFromServiceTree: (serviceId) => {
        const clinicIds = (get().serviceMap &&
            'get' in get().serviceMap &&
            get().serviceMap.get(serviceId)?.clinicIds) ||
            [];
        return clinicIds;
    },
}));
